import { Capacitor } from "@capacitor/core";
import { Dialog as NativeDialog } from "@capacitor/dialog";
import { t } from "../i18n";

// Alerts and confirmations as promises: the device's own dialog in the apps, as before, and a
// Dialog on the web, shown by DialogHost.

export interface AlertOptions {
  title: string;
  message: string;
  /** The button; OK when not given. */
  ok?: string;
  /** The web's button's classes: `btn` (the default) or, say, `btn btn-primary`. */
  okClass?: string;
  /** Show the app's own dialog in the apps too, as the React app's modals did. */
  inApp?: boolean;
}

export interface ConfirmOptions extends AlertOptions {
  /** The confirming button in the apps, where it differs from the web's (it said OK there). */
  nativeOk?: string;
  /** The cancelling button; Cancel when not given. */
  cancel?: string;
  /**
   * What confirming does. On the web the dialog stays open, with a spinner on its button, until
   * this is done; its error, if any, rejects the confirmation.
   */
  action?: () => Promise<unknown>;
}

export interface DialogRequest {
  readonly kind: "alert" | "confirm";
  readonly title: string;
  readonly message: string;
  readonly ok: string;
  readonly cancel: string;
  readonly okClass: string;
  /** Running the action. */
  busy: boolean;
  /** Answer it: confirmed or not. */
  settle(confirmed: boolean): void;
}

/** The web's open alerts and confirmations, oldest first. */
export const dialogs = $state<{ open: DialogRequest[] }>({ open: [] });

/**
 * Answer every open dialog as cancelled, as the session that asked ends: none may stay over the next
 * screen (a search's delete question names the patient), or act for whoever answers it next (sending
 * the last user's report). One whose action is running finishes as usual, unless `running`: then it
 * leaves the screen too, and still answers once its action is done.
 */
export function dismissDialogs({ running = false } = {}): void {
  for (const request of [...dialogs.open]) {
    if (!request.busy) {
      request.settle(false);
    } else if (running) {
      dialogs.open.splice(dialogs.open.indexOf(request), 1);
    }
  }
}

function show(kind: DialogRequest["kind"], options: ConfirmOptions): Promise<boolean> {
  return new Promise((resolve, reject) => {
    let settled = false;
    const close = () => {
      // dismissDialogs({ running: true }) may have taken it off the screen already.
      const index = dialogs.open.indexOf(request);
      if (index >= 0) {
        dialogs.open.splice(index, 1);
      }
    };
    dialogs.open.push({
      kind,
      title: options.title,
      message: options.message,
      ok: options.ok ?? t("generic.ok"),
      cancel: options.cancel ?? t("generic.cancel"),
      okClass: options.okClass ?? "btn",
      busy: false,
      settle(confirmed) {
        if (settled) {
          return;
        }
        settled = true;
        if (!confirmed || !options.action) {
          close();
          resolve(confirmed);
          return;
        }
        request.busy = true;
        options.action().then(
          () => {
            close();
            resolve(true);
          },
          (err: unknown) => {
            close();
            reject(err instanceof Error ? err : new Error(String(err)));
          },
        );
      },
    });
    // The reactive copy, which the host shows.
    const request = dialogs.open[dialogs.open.length - 1];
  });
}

export async function alert(options: AlertOptions): Promise<void> {
  if (Capacitor.isNativePlatform() && !options.inApp) {
    await NativeDialog.alert({
      title: options.title,
      message: options.message,
      buttonTitle: options.ok,
    });
    return;
  }
  await show("alert", options);
}

/** Resolves true when confirmed (after its `action`, if it has one). */
export async function confirm(options: ConfirmOptions): Promise<boolean> {
  if (Capacitor.isNativePlatform() && !options.inApp) {
    const { value } = await NativeDialog.confirm({
      title: options.title,
      message: options.message,
      okButtonTitle: options.nativeOk ?? options.ok,
      cancelButtonTitle: options.cancel,
    });
    if (value) {
      await options.action?.();
    }
    return value;
  }
  return show("confirm", options);
}

/** The React app's ErrorModal: the message, titled Error, with OK. It was the app's own dialog everywhere. */
export function showError(message: string, title = "Error"): Promise<void> {
  return alert({ title, message, okClass: "btn px-4", inApp: true });
}
